import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, ShieldCheck, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSeoTitle } from "@/lib/useSeoTitle";

const AMBIENT = "https://images.pexels.com/photos/8564636/pexels-photo-8564636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const TRIAL_ID_KEY = "saga_trial_id";
const TRIAL_ARCHETYPE_KEY = "saga_trial_archetype";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  useSeoTitle({
    title: "Create Your TalesTalesTales Account",
    description: "Create your TalesTalesTales account to save your AI Novel saga, continue from trial, and claim daily free turns.",
    path: "/register",
  });
  const [params] = useSearchParams();
  const fromTrial = params.get("from") === "trial";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [secQ, setSecQ] = useState("");
  const [secA, setSecA] = useState("");
  const [age, setAge] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!secQ || !secA) {
      return toast.error("Set a security question — needed for password recovery.");
    }
    const ageNum = parseInt(age, 10);
    if (!ageNum || ageNum < 1 || ageNum > 120) {
      return toast.error("Please enter your age.");
    }
    setBusy(true);
    try {
      await register(email, password, name, {
        security_question: secQ,
        security_answer: secA,
        age: ageNum,
      });
      toast.success("Your name is inscribed in the chronicle.");

      // Trial continuity — if the visitor came from the free trial, port the
      // saga into their new account so they can keep playing.
      const trialId = (params.get("trial_id") || localStorage.getItem(TRIAL_ID_KEY) || "").trim();
      if (trialId) {
        try {
          const r = await api.post("/trial/claim", { trial_id: trialId });
          if (r.data?.campaign_id) {
            localStorage.removeItem(TRIAL_ID_KEY);
            localStorage.removeItem(TRIAL_ARCHETYPE_KEY);
            toast.success("Your trial saga has been bound to your account.");
            nav(`/play/${r.data.campaign_id}`);
            return;
          }
        } catch (claimErr) {
          // Don't block the user if the claim fails — let them go to the
          // dashboard and re-try via the trial page if needed.
          console.warn("Trial claim failed", claimErr?.response?.data?.detail);
        }
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The scribe rejects this entry.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div id="main-content" className="relative min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-10">
        <div className="absolute inset-0 opacity-40">
          <img src={AMBIENT} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0d11]/70 via-[#0f0d11]/85 to-[#0f0d11]" />
        </div>

        <form
          onSubmit={submit}
          data-testid="register-form"
          className="relative w-full max-w-md parchment gold-border rounded-xl p-10 candle-glow rise"
        >
          <Flame className="w-8 h-8 text-amber-400 flicker mb-3" />
          <h1 className="font-display text-4xl text-amber-50 mb-2">Begin your chronicle</h1>
          <p className="font-body text-amber-100/60 mb-6">A name, a key, a recovery rune.</p>

          {fromTrial && (
            <div
              data-testid="register-from-trial-banner"
              className="mb-5 rounded-md border border-amber-500/40 bg-amber-900/15 px-3 py-2.5 flex items-start gap-2"
            >
              <Sparkles className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
              <div className="font-body text-xs text-amber-100/85 leading-relaxed">
                Loved your trial? Create a free account to <span className="gold-text">keep your saga going</span> —
                10 turns/day, unlimited campaigns, every narrator.
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Traveller&apos;s Name</Label>
              <Input
                data-testid="register-name-input"
                required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="The Pale Ranger"
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Email</Label>
              <Input
                data-testid="register-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="ranger@saga.tales"
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Password</Label>
              <Input
                data-testid="register-password-input"
                type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="At least 8 runes"
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Age</Label>
              <Input
                data-testid="register-age-input"
                type="number" required min={1} max={120}
                value={age} onChange={(e) => setAge(e.target.value)}
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="Your age"
              />
            </div>
            <div className="rounded-lg border border-amber-700/25 bg-[#15121a]/60 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-amber-300/80 font-ui text-[10px] uppercase tracking-widest">
                <ShieldCheck className="w-3 h-3" /> Security question (for password recovery)
              </div>
              <Input
                data-testid="register-sec-q"
                required value={secQ} onChange={(e) => setSecQ(e.target.value)}
                placeholder="e.g. What was your first pet's name?"
                className="bg-[#15121a] border-amber-700/30 text-amber-100"
                maxLength={200}
              />
              <Input
                data-testid="register-sec-a"
                required value={secA} onChange={(e) => setSecA(e.target.value)}
                placeholder="Answer (case-insensitive)"
                className="bg-[#15121a] border-amber-700/30 text-amber-100"
                maxLength={200}
              />
            </div>
            <Button
              data-testid="register-submit-btn"
              type="submit" disabled={busy}
              className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui candle-glow"
            >
              {busy ? "Inscribing..." : "Forge my hero"}
            </Button>
          </div>

          <p className="mt-6 text-center font-body text-amber-200/60 text-sm">
            Already chronicled?{" "}
            <Link data-testid="register-to-login-link" to="/login" className="text-amber-400 hover:text-amber-300">
              Sign in
            </Link>
          </p>
        </form>
      </div>
      <section className="max-w-3xl mx-auto px-6 py-10 text-center">
        <h2 className="font-display text-2xl text-amber-100 mb-3">What is TalesTalesTales?</h2>
        <p className="font-body text-amber-100/65 text-sm leading-relaxed">
          TalesTalesTales is a free interactive AI Novel where you become the main character. Forge a hero of
          any race and class, choose a setting (gritty high-fantasy, pirate, fey-touched, planar, sandbox, or
          your own custom realm), and let a literary narrator describe the world while you decide every action.
          Light RPG elements are along for the ride: occasional dice rolls, skills, equipment, inventory, and
          objectives — no rulebook required. Sign up takes a moment and gives you 10 free turns every day
          — no credit card required. Read more on the <Link to="/" className="gold-text hover:text-amber-200">home page</Link>{" "}
          or browse <Link to="/worlds" className="gold-text hover:text-amber-200">curated realms</Link> to find your starting hook.
        </p>
      </section>
    </div>
  );
}
